import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import { ErrorOwner } from '../../api/api';
import { getIssues, Issue } from '../../api/issues';
import { DossierAction, DossierNode, ROLE_LABEL } from '../../api/dossier';
import { previewRequeue, RequeuePreview } from '../data/cockpit';
import { ago } from '../data/format';
import { remember, usePoll } from '../data/usePoll';
import { Card, CheckRow, Owner, Spinner } from '../components/ui';
import { v2 } from '../paths';
import { issueCacheKey, issueLabel, NextStep, nextStep, objectName } from './issueModel';

const OWNER: Record<string, Owner> = { [ErrorOwner.Support]: 'us', [ErrorOwner.Customer]: 'cu', [ErrorOwner.Automatic]: 'au', [ErrorOwner.None]: 'au' };

const BADGE: Record<NextStep['who'], string> = { us: 'WIJ', cu: 'KLANT', au: '↻', done: '✓' };

/** One thing to do, as the issue panel says it: who, what now, why, what the scanner checked itself, and the button. */
const Step = ({
    step,
    subject,
    details,
    checks,
    checkedAt,
    open,
    offer,
}: {
    step: NextStep;
    subject: string;
    details?: string[];
    checks?: { text: string; ok: boolean }[];
    checkedAt?: string | null;
    open?: { to: string; state: unknown };
    offer?: { to: string; state: unknown };
}) => {
    const badge = step.who === 'done' ? 'au' : step.who;
    const okCount = (checks ?? []).filter((c) => c.ok).length;
    return (
        <div className="dsec">
            <div className={`next ${badge}`}>
                <span className={`who ${badge}`}>{BADGE[step.who]}</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
                    <div className="mut mono" style={{ fontSize: 12 }}>{subject}</div>
                    <div style={{ fontSize: 17, fontWeight: 600 }}>{step.title}</div>
                    {step.text && <div>{step.text}</div>}
                    {(details ?? []).map((d) => (
                        <div key={d} className="mut" style={{ fontSize: 13 }}>{d}</div>
                    ))}
                    {checks && checks.length > 0 && (
                        <details>
                            <summary className="mut" style={{ cursor: 'pointer', fontSize: 13 }}>
                                Zelf nagekeken: {okCount} van {checks.length} in orde{checkedAt ? ` · ${ago(checkedAt)} geleden` : ''}
                            </summary>
                            <div className="checks" style={{ marginTop: 8 }}>
                                {checks.map((c, n) => (
                                    <CheckRow key={n} tone={c.ok ? 'ok' : 'bad'} label={c.text} />
                                ))}
                            </div>
                        </details>
                    )}
                    {(offer || open) && (
                        <div className="hrow">
                            {offer && step.actionLabel && (
                                <Link className="btn pri" to={offer.to} state={offer.state}>
                                    {step.actionLabel}
                                </Link>
                            )}
                            {open && (
                                <Link className="btn ghost" to={open.to} state={open.state}>
                                    Issue openen
                                </Link>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

/**
 * "Wat nu" of one object: one next step per problem that is still open, from the backlog's issue and its requeue
 * verdict, the same as the issue panel says it. Only a problem without an issue (a difference between Chargebee and
 * Exact, an error without a rule) falls back on the dossier's own words.
 *
 * Before, the dossier's interpretations went here as they were: the old failure of an invoice that got through ("Opgelost:
 * staat inmiddels in Exact"), the generic text of a 401 ("de scanner doet dat zelf", while offering again is off) and
 * "zoek uit welke kant klopt" for a difference the stuck payment itself explained; three steps that contradicted each other.
 */
export const ObjectNextSteps = ({
    customer,
    environment,
    objectId,
    actions,
    nodes,
}: {
    customer: string;
    environment: string;
    objectId: string;
    actions: DossierAction[];
    nodes: DossierNode[];
}) => {
    const axios = useAxiosSwagger();
    const issues = usePoll<Issue[]>(
        axios ? async (s) => (await getIssues(axios, { customer, environment, q: objectId, status: 'open' }, s)).items ?? [] : null,
        60000,
        [axios, customer, environment, objectId],
        `issues-of/${customer}/${environment}/${objectId}`
    );
    const keys = (issues.data ?? []).map((i) => i.key).join(',');
    const previews = usePoll<Record<string, RequeuePreview>>(
        axios && keys
            ? async (s) => {
                  const list = await previewRequeue(axios, (issues.data ?? []).map((i) => ({ customer, environment, key: i.key })), s);
                  return Object.fromEntries(list.map((p) => [p.key, p]));
              }
            : null,
        60000,
        [axios, customer, environment, keys],
        `previews-of/${customer}/${environment}/${keys}`
    );

    // The issue panel opens with what is known here, instead of empty.
    useEffect(() => (issues.data ?? []).forEach((i) => remember(issueCacheKey(i.customer, i.environment, i.key), i)), [issues.data]);

    const here = { from: window.location.pathname, label: objectId };
    const relationOf = (key: string) => nodes.find((n) => n.key === key)?.relationId ?? key.split('|').pop() ?? key;

    // The dossier's steps that an open issue covers are said by that issue; the others stay as the dossier has them.
    const { fromIssues, rest } = useMemo(() => {
        const open = issues.data ?? [];
        const covered = new Set<string>();
        const steps = actions.flatMap((a) => {
            const mine = open.filter((i) => a.nodeKeys.some((k) => relationOf(k) === i.relationId));
            mine.forEach((i) => covered.add(i.key));
            return mine.map((i) => ({ issue: i, details: a.details }));
        });
        // An open issue of this object the dossier did not name (outside its period, for one).
        open.filter((i) => !covered.has(i.key)).forEach((i) => steps.push({ issue: i, details: [] }));
        const unique = steps.filter((s, n) => steps.findIndex((x) => x.issue.key === s.issue.key) === n);
        const restActions = actions.filter((a) => !a.nodeKeys.some((k) => open.some((i) => i.relationId === relationOf(k))));
        return { fromIssues: unique, rest: restActions };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [issues.data, actions, nodes]);

    if (fromIssues.length === 0 && rest.length === 0) return null;

    return (
        <Card
            title="Wat nu"
            right={
                issues.loading || previews.loading ? (
                    <span className="mut hrow" style={{ fontSize: 12, gap: 6 }}>
                        <Spinner />
                        controles ophalen…
                    </span>
                ) : undefined
            }
        >
            {fromIssues.map(({ issue, details }) => {
                const step = nextStep(issue, previews.data?.[issue.key]);
                const to = v2.issue(issue.customer, issue.environment, issue.key);
                const state = { from: here.from, label: here.label };
                return (
                    <Step
                        key={issue.key}
                        step={step}
                        subject={`${objectName(issue.objectType)} ${issueLabel(issue) === issue.relationId ? issue.relationId : `${issue.relationId} · factuur ${issueLabel(issue)}`}`}
                        details={details}
                        checks={issue.checks}
                        checkedAt={issue.checksAt}
                        open={{ to, state }}
                        offer={step.action ? { to: step.action === 'relink' || step.action === 'link' ? `${to}?actie=${step.action}` : `${to}?aanbieden=1`, state } : undefined}
                    />
                );
            })}
            {rest.map((a) => {
                const who = OWNER[a.owner] ?? 'us';
                const subject = a.nodeKeys
                    .map((k) => nodes.find((n) => n.key === k))
                    .filter((n): n is DossierNode => !!n)
                    .slice(0, 2)
                    .map((n) => `${ROLE_LABEL[n.role]} ${n.relationId}`)
                    .join(', ');
                return (
                    <Step
                        key={a.code}
                        step={{ title: a.title, who, text: a.action ?? undefined }}
                        subject={subject}
                        details={[...a.details, ...a.steps]}
                    />
                );
            })}
        </Card>
    );
};
